import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, PlayCircle, CheckCircle2, XCircle, Clock, DollarSign,
  StopCircle, User, LogIn, LogOut, FileOutput, GitBranch, AlertTriangle,
  Gavel, Swords, GitMerge, UserCheck, AlertCircle, Download
} from "lucide-react";
import { runsApi, streamRun } from "../../lib/workflows";
import { workforceApi } from "../../lib/workforce";
import { API } from "../../lib/api";
import Markdown from "../../components/Markdown";

const STATUS_META = {
  queued:        { color: "#94A3B8", label: "Queued" },
  running:       { color: "#3B82F6", label: "Running" },
  waiting_human: { color: "#F59E0B", label: "Waiting for human" },
  completed:     { color: "#10B981", label: "Completed" },
  failed:        { color: "#EF4444", label: "Failed" },
  cancelled:     { color: "#64748B", label: "Cancelled" },
};

const NODE_ICON = {
  start: LogIn, worker: User, output: FileOutput, end: LogOut,
  reviewer: Gavel, condition: GitMerge, human_approval: UserCheck, debate: Swords,
};

export default function RunDetail() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [workers, setWorkers] = useState({});
  const [brains, setBrains] = useState({});
  const [events, setEvents] = useState([]);
  const [streamingDeltas, setStreamingDeltas] = useState({}); // node_id -> accumulated text
  const streamRef = useRef(null);

  // Initial load + open SSE
  // Refetch + reopen the stream — used both on initial load and after approval to catch resume events
  const startStream = useCallback((currentRun) => {
    streamRef.current?.close?.();
    // If terminal (including waiting_human), no need to stream — poll on demand instead
    if (["completed", "failed", "cancelled", "waiting_human"].includes(currentRun.status)) {
      streamRef.current = null;
      return;
    }
    streamRef.current = streamRun(id, {
      onEvent: (ev) => {
        if (ev.type === "worker_delta") {
          setStreamingDeltas((d) => ({ ...d, [ev.node_id]: (d[ev.node_id] || "") + ev.content }));
          return;
        }
        if (ev.type === "stream_end") return;
        setEvents((es) => {
          const key = `${ev.ts || ""}-${ev.type}-${ev.node_id || ""}`;
          if (es.some((x) => `${x.ts || ""}-${x.type}-${x.node_id || ""}` === key)) return es;
          return [...es, ev];
        });
        if (["run_completed", "run_failed", "approval_required"].includes(ev.type)) {
          runsApi.get(id).then((r2) => { setRun(r2); setEvents(r2.events || []); });
        }
      },
      onEnd: () => { streamRef.current = null; },
    });
  }, [id]);

  const refreshAndMaybeStream = useCallback(async () => {
    const [r, ws, bs] = await Promise.all([
      runsApi.get(id),
      workforceApi.listWorkers(),
      workforceApi.listBrains(),
    ]);
    setRun(r);
    setEvents(r.events || []);
    const wm = {}; (ws.workers || []).forEach((w) => { wm[w.worker_id] = w; });
    setWorkers(wm);
    const bm = {}; (bs.brains || []).forEach((b) => { bm[b.id] = b; });
    setBrains(bm);
    startStream(r);
  }, [id, startStream]);

  useEffect(() => {
    refreshAndMaybeStream();
    return () => streamRef.current?.close?.();
  }, [refreshAndMaybeStream]);

  if (!run) return <div className="p-10 text-sm text-slate-400">Loading run…</div>;

  const snapshot = run.workflow_snapshot || {};
  const snapshotNodes = snapshot.nodes || [];
  const nodeById = {};
  snapshotNodes.forEach((n) => { nodeById[n.node_id] = n; });

  const meta = STATUS_META[run.status] || { color: "#64748B", label: run.status };
  const duration = run.started_at && run.completed_at
    ? Math.round((new Date(run.completed_at) - new Date(run.started_at)) / 1000)
    : run.started_at
    ? Math.round((Date.now() - new Date(run.started_at).getTime()) / 1000)
    : null;

  const cancel = async () => {
    if (!window.confirm("Cancel this run?")) return;
    await runsApi.cancel(id);
    runsApi.get(id).then(setRun);
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="run-detail-page">
      <Link to="/workforce/runs" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold mb-3" data-testid="back-to-runs">
        <ArrowLeft size={12} strokeWidth={3} /> Back to runs
      </Link>

      {/* Header */}
      <div className="brut-card p-5 mb-6" data-testid="run-header">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Run</div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold leading-tight">
              {run.workflow_name || "(deleted workflow)"}
            </h1>
            <div className="text-xs text-slate-500 font-mono mt-1">#{run.run_id}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border-2 flex items-center gap-1.5"
                  style={{ borderColor: meta.color, color: meta.color, backgroundColor: meta.color + "1F" }}
                  data-testid="run-status">
              {run.status === "running" && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dot-blink" />}
              {meta.label}
            </span>
            {["queued", "running"].includes(run.status) && (
              <button onClick={cancel} className="brut-btn brut-btn-outline text-sm" data-testid="cancel-run">
                <StopCircle size={14} strokeWidth={2.5} /> Cancel
              </button>
            )}
            <button onClick={() => exportRun(id, "md")} className="brut-btn brut-btn-outline text-sm" data-testid="export-run-btn">
              <Download size={14} strokeWidth={2.5} /> Export MD
            </button>
            <button onClick={() => exportRun(id, "pdf")} className="brut-btn brut-btn-outline text-sm" data-testid="export-run-pdf-btn">
              <Download size={14} strokeWidth={2.5} /> Export PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCell icon={Clock}     label="Runtime" value={duration !== null ? `${duration}s` : "—"} />
          <MetricCell icon={DollarSign} label="Cost"    value={`$${(run.total_cost || 0).toFixed(4)}`} />
          <MetricCell icon={PlayCircle} label="Nodes"   value={(run.node_runs || []).length} />
          <MetricCell icon={CheckCircle2} label="Brains" value={(run.models_used || []).length} />
        </div>

        {run.input?.input && (
          <div className="mt-4 p-3 rounded-lg border-2 border-slate-900 bg-amber-50">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Input</div>
            <div className="text-sm text-slate-800">"{run.input.input}"</div>
          </div>
        )}

        {run.error && (
          <div className="mt-3 p-3 rounded-lg border-2 border-red-500 bg-red-50 text-red-800 text-sm flex items-start gap-2" data-testid="run-error">
            <AlertTriangle size={14} strokeWidth={3} className="shrink-0 mt-0.5" />
            <span>{run.error}</span>
          </div>
        )}
      </div>

      {run.status === "waiting_human" && run.pending_approval && (
        <ApprovalCard runId={id} pending={run.pending_approval} onDone={refreshAndMaybeStream} />
      )}

      {/* Two-column: timeline (main) + final output (side) */}
      <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,320px)] gap-6">
        {/* Timeline */}
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">Timeline</div>
          <div className="space-y-3" data-testid="run-timeline">
            {events.length === 0 && (
              <div className="text-sm text-slate-400 italic">No events yet…</div>
            )}
            {events.map((ev, i) => (
              <TimelineEvent
                key={i}
                ev={ev}
                node={nodeById[ev.node_id]}
                nodeRun={(run.node_runs || []).find((nr) => nr.node_run_id === ev.node_run_id)}
                streamingText={streamingDeltas[ev.node_id]}
                workers={workers}
                brains={brains}
              />
            ))}
          </div>
        </div>

        {/* Final output */}
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">Final output</div>
          <div className="brut-card p-4 bg-amber-50" data-testid="final-output">
            {run.final_output?.content ? (
              <Markdown text={run.final_output.content} />
            ) : (
              <div className="text-sm text-slate-400 italic">
                {run.status === "running" ? "Waiting for workflow to finish…" : "No final output yet."}
              </div>
            )}
          </div>

          {run.workers_used?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Workers</div>
              <div className="flex flex-wrap gap-1.5">
                {run.workers_used.map((wid) => {
                  const w = workers[wid];
                  return (
                    <span key={wid} className="text-xs flex items-center gap-1.5 border-2 border-slate-900 rounded-full px-2 py-0.5 bg-white">
                      {w ? `${w.avatar} ${w.name}` : wid}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {run.models_used?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Brains used</div>
              <div className="flex flex-wrap gap-1.5">
                {run.models_used.map((bid) => {
                  const b = brains[bid];
                  return (
                    <span key={bid} className="text-xs flex items-center gap-1.5 border-2 rounded-full px-2 py-0.5"
                          style={{ borderColor: b?.color || "#0F172A", color: b?.color || "#0F172A", backgroundColor: (b?.color || "#0F172A") + "1F" }}>
                      {b?.name || bid}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCell({ icon: Icon, label, value }) {
  return (
    <div className="border-2 border-slate-900 rounded-xl p-3 bg-white">
      <div className="flex items-center gap-1.5 text-slate-500 mb-1">
        <Icon size={12} strokeWidth={2.5} />
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="font-heading font-bold text-xl leading-none">{value}</div>
    </div>
  );
}

function TimelineEvent({ ev, node, nodeRun, streamingText, workers, brains }) {
  const t = ev.ts ? new Date(ev.ts).toLocaleTimeString() : "";
  const nodeType = node?.type;
  const Icon = nodeType ? NODE_ICON[nodeType] || GitBranch : null;

  const isNodeStart = ev.type === "node_started";
  const isNodeDone  = ev.type === "node_completed";
  const isNodeFail  = ev.type === "node_failed";
  const isRunStart  = ev.type === "run_started";
  const isRunResume = ev.type === "run_resumed";
  const isRunDone   = ev.type === "run_completed";
  const isRunFail   = ev.type === "run_failed";
  const isApprovalReq   = ev.type === "approval_required";
  const isApprovalDone  = ev.type === "approval_resolved";

  if (isRunStart)  return <TimelinePill t={t} color="#3B82F6" label="Run started"                              testid="ev-run-started" />;
  if (isRunResume) return <TimelinePill t={t} color="#3B82F6" label="Run resumed after approval"               testid="ev-run-resumed" />;
  if (isRunDone)   return <TimelinePill t={t} color="#10B981" label={`Run completed · $${(ev.total_cost || 0).toFixed(4)}`} testid="ev-run-completed" />;
  if (isRunFail)   return <TimelinePill t={t} color="#EF4444" label={`Run failed — ${ev.error || ""}`}         testid="ev-run-failed" />;
  if (isApprovalReq)  return <TimelinePill t={t} color="#F59E0B" label={`Awaiting human approval — ${ev.prompt || ""}`} testid="ev-approval-required" />;
  if (isApprovalDone) return <TimelinePill t={t} color="#10B981" label={`Human ${ev.decision}${ev.instructions ? ` — "${ev.instructions.slice(0, 60)}"` : ""}`} testid="ev-approval-resolved" />;

  const worker = nodeRun?.worker_id ? workers[nodeRun.worker_id] : null;
  const brain  = nodeRun?.brain_id  ? brains[nodeRun.brain_id]  : null;
  const output = nodeRun?.output;
  // Build display text based on node type
  let bodyText = streamingText;
  if (!bodyText && output) {
    if (typeof output === "string") bodyText = output;
    else if (output.decision) bodyText = `**${output.decision}** · score ${output.score ?? "—"}\n\n${output.summary || ""}${output.revision_instructions ? "\n\n> " + output.revision_instructions : ""}${(output.issues || []).length ? "\n\n" + output.issues.map((i) => "- " + i).join("\n") : ""}`;
    else if (typeof output === "object") bodyText = JSON.stringify(output, null, 2);
  }
  const handleBadge = nodeRun?.handle;

  return (
    <div className="brut-card p-4" data-testid={`ev-node-${ev.node_id}-${ev.type}`}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon size={14} strokeWidth={2.5} className="text-slate-600" />}
        <span className="font-heading font-bold text-sm">
          {nodeType?.toUpperCase()}{worker ? ` · ${worker.avatar} ${worker.name}` : ""}
        </span>
        {brain && (
          <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full border"
                style={{ borderColor: brain.color, color: brain.color, backgroundColor: brain.color + "1F" }}>
            {brain.name}
          </span>
        )}
        {isNodeDone && handleBadge && (
          <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full border"
                style={{ borderColor: handleColor(handleBadge), color: handleColor(handleBadge), backgroundColor: handleColor(handleBadge) + "1F" }}>
            → {handleBadge}
          </span>
        )}
        <span className="ml-auto text-[10px] text-slate-400 font-mono">{t}</span>
      </div>

      <div className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-1">
        {isNodeStart && "Started"}
        {isNodeDone && `Completed · $${(nodeRun?.cost || 0).toFixed(4)}`}
        {isNodeFail && `Failed — ${ev.error || ""}`}
      </div>

      {bodyText && (
        <div className="mt-2 max-h-64 overflow-y-auto text-sm text-slate-800">
          <Markdown text={typeof bodyText === "string" ? bodyText : JSON.stringify(bodyText)} />
        </div>
      )}
    </div>
  );
}

function handleColor(h) {
  return { pass: "#059669", approve: "#059669", true: "#059669",
           revise: "#D97706",
           reject: "#DC2626", false: "#DC2626" }[h] || "#0F172A";
}

async function exportRun(runId, fmt = "md") {
  const token = localStorage.getItem("token");
  try {
    const res = await fetch(`${API}/runs/${runId}/export.${fmt}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = fmt === "pdf"
      ? await res.blob()
      : new Blob([await res.text()], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `run-${runId}.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    alert(e.message || "Export failed");
  }
}

function TimelinePill({ t, color, label, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <span className="w-2 h-2 rounded-full border-2 border-slate-900" style={{ backgroundColor: color }} />
      <span className="font-heading font-bold text-sm">{label}</span>
      <span className="text-[10px] text-slate-400 font-mono ml-auto">{t}</span>
    </div>
  );
}

/* -------------------- Approval card -------------------- */
function ApprovalCard({ runId, pending, onDone }) {
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await new Promise((r) => setTimeout(r, 400)); onDone?.(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="brut-card p-5 bg-pink-50 mb-6 border-slate-900" data-testid="approval-card">
      <div className="flex items-center gap-2 mb-3">
        <UserCheckAlt />
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Human approval required</div>
          <div className="font-heading font-bold text-lg leading-tight">{pending.prompt}</div>
        </div>
      </div>
      {pending.output_being_reviewed && (
        <div className="mb-3 max-h-56 overflow-y-auto brut-card p-3 bg-white" data-testid="approval-preview">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Output being reviewed</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">{pending.output_being_reviewed}</div>
        </div>
      )}
      <textarea
        rows={2} value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        placeholder="Optional note or revision instructions…"
        className="brut-input text-sm mb-3"
        data-testid="approval-instructions"
      />
      <div className="flex gap-2 flex-wrap">
        <button disabled={busy} onClick={() => act(() => runsApi.approve(runId, instructions))}
                className="brut-btn text-sm" data-testid="approve-btn">
          ✓ Approve
        </button>
        <button disabled={busy || !instructions.trim()} onClick={() => act(() => runsApi.instruct(runId, instructions))}
                className="brut-btn brut-btn-outline text-sm disabled:opacity-50" data-testid="revise-btn">
          ↺ Request revision
        </button>
        <button disabled={busy} onClick={() => act(() => runsApi.reject(runId, instructions))}
                className="brut-btn brut-btn-outline text-sm" style={{ borderColor: "#B91C1C", color: "#B91C1C", boxShadow: "3px 3px 0 #B91C1C" }} data-testid="reject-btn">
          ✕ Reject
        </button>
      </div>
    </div>
  );
}

function UserCheckAlt() {
  return (
    <div className="w-9 h-9 rounded-xl border-2 border-slate-900 bg-white flex items-center justify-center text-pink-700">
      <AlertCircle size={18} strokeWidth={2.5} />
    </div>
  );
}
